import React, { useEffect, useState, useCallback, useRef } from 'react';
import {
  Chart as ChartJS,
  ArcElement,
  Tooltip,
  Legend
} from 'chart.js';
import { Doughnut } from 'react-chartjs-2';
import { Box, Typography, CircularProgress, Grid, Chip, Card, CardContent } from '@mui/material';
import ExpandableCard from './ExpandableCard';
import { io } from 'socket.io-client';

// Plugin para texto central de la dona
const centerTextPlugin = {
  id: 'centerText',
  afterDraw(chart) {
    if (chart.config.type !== 'doughnut') return;
    const { ctx, chartArea } = chart;
    if (!chartArea) return;
    const data = chart.data.datasets[0]?.data || [];
    const total = data.reduce((a, b) => a + Number(b), 0);
    const cx = (chartArea.left + chartArea.right) / 2;
    const cy = (chartArea.top + chartArea.bottom) / 2;
    ctx.save();
    ctx.font = 'bold 15px Roboto, sans-serif';
    ctx.fillStyle = '#ffffff';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText(Math.round(total).toLocaleString('es-CO'), cx, cy - 7);
    ctx.font = '10px Roboto, sans-serif';
    ctx.fillStyle = '#aaaaaa';
    ctx.fillText('metros', cx, cy + 10);
    ctx.restore();
  }
};

ChartJS.register(ArcElement, Tooltip, Legend, centerTextPlugin);

const SOCKET_URL = process.env.REACT_APP_SOCKET_URL || 'https://pochelo.online';

const COLORES = [
  '#00cba0', '#f4a261', '#e76f51', '#264653', '#2a9d8f',
  '#e9c46a', '#457b9d', '#a8dadc', '#e63946', '#6d6875'
];

// Fallback: refrescar si en 5 min no llegó ningún evento socket
const FALLBACK_MS = 5 * 60 * 1000;

function totalMetros(datos) {
  return datos.reduce((s, d) => s + d.metros, 0);
}

function horaActual() {
  return new Date().toLocaleTimeString('es-CO', {
    hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false,
    timeZone: 'America/Bogota'
  });
}

const DonutMetrosPorTurno = ({ darkMode = true }) => {
  const [datos, setDatos] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [ultimaActualizacion, setUltimaActualizacion] = useState('');
  const fallbackTimer = useRef(null);

  const fetchDatos = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await fetch('/api/metros-por-turno', {
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });
      if (!res.ok) throw new Error('Error al obtener datos');
      const json = await res.json();
      setDatos(json);
      setUltimaActualizacion(horaActual());
      setError('');
    } catch (e) {
      setError('No se pudieron cargar los datos de producción.');
    } finally {
      setLoading(false);
    }
  }, []);

  // Reinicia el temporizador de fallback cada vez que llega un evento socket
  const resetFallback = useCallback(() => {
    if (fallbackTimer.current) clearTimeout(fallbackTimer.current);
    fallbackTimer.current = setTimeout(() => {
      fetchDatos();
      resetFallback();
    }, FALLBACK_MS);
  }, [fetchDatos]);

  useEffect(() => {
    // Carga inicial
    fetchDatos();

    const socket = io(SOCKET_URL, {
      auth: { token: localStorage.getItem('token') }
    });

    // Máquinas 1-7: el mqttLogger emite 'ultimoAcumuladoHoy' después de guardar en BD
    socket.on('ultimoAcumuladoHoy', () => {
      fetchDatos();
      resetFallback();
    });

    // Máquinas 8-11: emiten su evento propio cuando llega dato nuevo
    ['mqttMachine8', 'mqttMachine9', 'mqttMachine10', 'mqttMachine11'].forEach(ev => {
      socket.on(ev, () => {
        fetchDatos();
        resetFallback();
      });
    });

    // Fallback inicial por si no hay ningún evento socket
    resetFallback();

    return () => {
      socket.disconnect();
      if (fallbackTimer.current) clearTimeout(fallbackTimer.current);
    };
  }, [fetchDatos, resetFallback]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress size={32} />
      </Box>
    );
  }

  if (error) {
    return (
      <Typography variant="body2" color="text.secondary" align="center" sx={{ py: 2 }}>
        {error}
      </Typography>
    );
  }

  const turnos = datos?.turnos || [];
  // Siempre mostrar al menos turno 1 y 2; combinar con lo que venga del backend
  const turnosBase = [1, 2];
  const turnoNums = Array.from(new Set([...turnosBase, ...turnos.map(t => t.turno)])).sort((a, b) => a - b);
  const turnosRender = turnoNums.map(num => {
    const found = turnos.find(t => t.turno === num);
    return { turno: num, datos: found?.datos || [] };
  });

  const t = {
    cardBg:     darkMode ? '#071525' : '#ffffff',
    cardBg2:    darkMode ? '#050e1e' : '#e8edf2',
    text:       darkMode ? '#8ab0cc' : '#1a2b3c',
    textMain:   darkMode ? '#ccc'    : '#1a2b3c',
    textMuted:  darkMode ? '#888'    : '#6b7280',
    border:     darkMode ? '1px solid rgba(40,120,255,0.28)' : '1px solid rgba(0,0,0,0.1)',
    shadow:     darkMode
      ? '0 8px 32px rgba(0,0,0,0.85), 0 2px 8px rgba(0,0,0,0.6), inset 0 1px 0 rgba(60,140,255,0.18)'
      : '0 4px 16px rgba(0,0,0,0.1), 0 1px 4px rgba(0,0,0,0.06)',
    chipBg:     darkMode ? '#1e3a2f' : '#d1fae5',
    chipColor:  darkMode ? '#00cba0' : '#065f46',
    legendEmpty: darkMode ? '#555'   : '#9ca3af',
    legendFull:  darkMode ? '#ccc'   : '#374151',
    turnoColor:  darkMode ? '#00cba0': '#059669',
    turnoEmpty:  darkMode ? '#555'   : '#9ca3af',
    vacioBg:    darkMode ? '#050e1e' : '#e8edf2',
    vacioBorde: darkMode ? 'rgba(40,120,255,0.28)' : 'rgba(0,0,0,0.1)',
  };

  return (
    <Box sx={{ width: '100%', flex: 1, display: 'flex', flexDirection: 'column', gap: 1 }}>
      {/* Header general */}
      <Card sx={{ backgroundColor: t.cardBg, color: t.text, boxShadow: t.shadow, border: t.border, borderRadius: '10px' }}>
        <CardContent sx={{ p: { xs: 0.25, sm: 1 }, pb: { xs: 0.25, sm: 1 } }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.5 }}>
            <Typography variant="subtitle2" align="center" sx={{ color: t.textMain, fontFamily: 'Roboto', fontWeight: 500 }}>
              Metros por Referencia — {datos?.fecha}
            </Typography>
            <Typography variant="caption" sx={{ color: t.textMuted, fontSize: '0.65rem' }}>
              Acumulado del día hasta el momento
            </Typography>
            {ultimaActualizacion && (
              <Chip
                label={`Actualizado: ${ultimaActualizacion}`}
                size="small"
                sx={{ bgcolor: t.chipBg, color: t.chipColor, fontSize: '0.65rem' }}
              />
            )}
          </Box>
        </CardContent>
      </Card>

      {/* Un Card por turno */}
      <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 1, minHeight: 0 }}>
      {turnosRender.map((turno) => {
        const sinDatos = turno.datos.length === 0;
        const labels = sinDatos ? ['Sin producción'] : turno.datos.map(d => d.referencia);
        const values = sinDatos ? [1] : turno.datos.map(d => d.metros);
        const colors = sinDatos ? [t.vacioBg] : turno.datos.map((_, i) => COLORES[i % COLORES.length]);

        const chartData = {
          labels,
          datasets: [
            {
              data: values,
              backgroundColor: colors,
              borderColor: sinDatos ? t.vacioBorde : 'transparent',
              borderWidth: sinDatos ? 1 : 0,
              hoverOffset: sinDatos ? 0 : 6
            }
          ]
        };

        const options = {
          responsive: true,
          maintainAspectRatio: true,
          cutout: '68%',
          layout: { padding: 0 },
          plugins: {
            legend: {
              position: 'right',
              labels: {
                color: sinDatos ? t.legendEmpty : t.legendFull,
                font: { size: 10 },
                padding: 6,
                boxWidth: 10,
                usePointStyle: true,
                pointStyleWidth: 8
              }
            },
            tooltip: sinDatos
              ? { enabled: false }
              : {
                  callbacks: {
                    label: (ctx) => ` ${ctx.label}: ${ctx.parsed.toLocaleString('es-CO')} m`
                  }
                }
          }
        };

        return (
          <ExpandableCard key={turno.turno} modalWidth={{ xs: '45vw', md: '20vw' }} modalHeight={{ xs: '45vw', md: '20vw' }} sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
            <Card sx={{ backgroundColor: t.cardBg, color: t.text, flex: 1, display: 'flex', flexDirection: 'column', boxShadow: t.shadow, border: t.border, borderRadius: '10px' }}>
              <CardContent sx={{ p: { xs: 0.25, sm: 1 }, pb: { xs: 0.25, sm: 1 }, flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                <Typography
                  variant="caption"
                  sx={{ color: sinDatos ? t.turnoEmpty : t.turnoColor, fontWeight: 700, display: 'block', textAlign: 'center', mb: 0.5 }}
                >
                  Turno {turno.turno}{sinDatos ? ' — sin datos' : ''}
                </Typography>
                <Box sx={{ width: '60%', mx: 'auto' }}>
                  <Doughnut data={chartData} options={options} />
                </Box>
              </CardContent>
            </Card>
          </ExpandableCard>
        );
      })}
      </Box>
    </Box>
  );
};

export default DonutMetrosPorTurno;
